import { indexOf } from 'lodash';
import { unitsComplete, parseGoal } from '@/goal';
import { parseHistory } from '@/goal/history';
import { asyncObject } from '@/async';
import { debounceByKey } from '@/util';
import dl, { keyFor } from '@/dataLayer';
const dlGoalKey = keyFor('goal');
const dlHistoryKey = keyFor('history');

export default {
    namespaced: true,
    state: {
        keys: [],
        archiveKeys: [],
        asyncHistory: {},
        asyncGoals: {},
    },
    actions: {
        async boot({ commit }) {
            const [keys, archiveKeys] = await Promise.all([
                dl.get('goals').then(x => x ?? []),
                dl.get('archive').then(x => x ?? []),
            ]);
			
            commit('loadKeys', { keys, archiveKeys });
        },

        async add({commit, dispatch}, {goal, historyCollection}) {
            commit('add', { goal, historyCollection });
            await dispatch('saveGoal', { goal, historyCollection });
            await dispatch('saveKeys');
        },

        async update({commit, dispatch}, { goal, historyCollection }) {
            commit('update', { goalKey: goal.key, goal, historyCollection });
            await dispatch('saveGoal', { goal, historyCollection });
        },
        
        async archive({commit, dispatch}, goal) {
            commit('archive', goal);
            await dispatch('saveGoal', {goal});
            await dispatch('saveKeys');
        },
        
        async restore({commit, dispatch}, goal) {
            commit('restore', goal);
            await dispatch('saveGoal', {goal});
            await dispatch('saveKeys');
        },
        
        async delete({commit, dispatch }, goal) {
            commit('delete', goal);

            await Promise.all([
                dl.remove(dlGoalKey(goal.key)),
                dl.remove(dlHistoryKey(goal.key)),
            ]);

            await dispatch('saveKeys');
        },

        // loads a Goal from storage and returns it async
        // uses asyncObject under the hood but does not return one here
        loadGoal({ commit, state }, goalKey) {
            let result = state.asyncGoals[goalKey];
            if (result == null) {
                commit('loadGoalBegin', goalKey);
                result = state.asyncGoals[goalKey];
                dl.get(dlGoalKey(goalKey))
                    .then(parseGoal)
                    .then(goal => commit('update', { goalKey, goal }));
            }

            return result.wait();
        },
		
        // loads a HistoryCollection from storage and returns it async
        // uses asyncObject under the hood but does not return one here
        loadHistory({ commit, state}, goalKey) {
            let result = state.asyncHistory[goalKey];
            if (result == null) {
                commit('loadHistoryBegin', goalKey);
                result = state.asyncHistory[goalKey];
                dl.get(dlHistoryKey(goalKey))
                    .then(parseHistory)
                    .then(historyCollection => commit('update', { goalKey, historyCollection }));
            }

            return result.wait();
        },
		
        saveGoal(_, {goal, historyCollection}) {
            debounceByKey(goal.key, 1000, async function commit() {
                await dl.set(dlGoalKey(goal.key), goal);
                if (historyCollection != null)
                    await dl.set(dlHistoryKey(goal.key), historyCollection);
            });
        },

        async saveKeys({state}) {
            debounceByKey('keys', 300, async function commit() {
                return Promise.all([
                    dl.set('goals', state.keys),
                    dl.set('archive', state.archiveKeys),
                ]);
            });
        },
    },
    mutations: {
        // insert a pending async goal
        loadGoalBegin({asyncGoals}, goalKey) {
            asyncGoals[goalKey] = asyncObject.pending();
        },

        // insert a pending async historycollection
        loadHistoryBegin({asyncHistory}, goalKey) {
            asyncHistory[goalKey] = asyncObject.pending();
        },

        // overwrite the goal keys that exist in the store
        loadKeys(state, {keys, archiveKeys}) {
            state.keys = keys;
            state.archiveKeys = archiveKeys;
        },

        // add a goal to the store
        // assumes that the goal does not already exist in the store
        // if the goal already exists, it will be overwritten
        add({ keys, archiveKeys, asyncGoals, asyncHistory }, {goal, historyCollection}) {
            if (indexOf(keys, goal.key) < 0)
                keys.push(goal.key);

            if (goal.archived && indexOf(archiveKeys, goal.key) < 0)
                archiveKeys.push(goal.key);

            asyncGoals[goal.key] = asyncObject.complete(goal);
            asyncHistory[goal.key] = asyncObject.complete(historyCollection);
        },

        // update a goal and/or historycollection in the store
        // update goal.unitsComplete with the value from historycollection
        update({asyncGoals, asyncHistory}, {goalKey, goal, historyCollection}) {
            if (goal != null && historyCollection != null)
                goal.unitsComplete = unitsComplete(historyCollection);
			
            if (goal != null)
                asyncGoals[goalKey].value = goal;

            if (historyCollection != null)
                asyncHistory[goalKey].value = historyCollection;
        },
        
        // set a goal's archived property to true
        // add the key to archivekeys
        archive({keys, archiveKeys}, goal) {
            if (indexOf(keys, goal.key) >= 0) 
                goal.archived = true;

            if (indexOf(archiveKeys, goal.key) < 0)
                archiveKeys.push(goal.key);            
        },
        
        // set a goal's archived property to false
        // remove the key from archivekeys
        restore({keys, archiveKeys}, goal) {
            if (indexOf(keys, goal.key) >= 0) 
                goal.archived = false;
			
            const archiveIndex = indexOf(archiveKeys, goal.key);
            if (archiveIndex >= 0)
                archiveKeys.splice(archiveIndex, 1);
        },

        // delete an archived goal
        // remove it from the goals store
        // remove the corresponding historycollection
        // remove key from keys and archivekeys
        delete({keys, archiveKeys, asyncGoals, asyncHistory}, goal) {
            const index = indexOf(keys, goal.key);
            const archiveIndex = indexOf(archiveKeys, goal.key);
			
            // only remove if it's already been archived
            if (index >= 0 && archiveIndex >= 0 && goal.archived) {
                keys.splice(index, 1);
                archiveKeys.splice(archiveIndex, 1);
				
                delete asyncGoals[goal.key];
                delete asyncHistory[goal.key];
            }
        },

        // clear all keys
        // clear all async state
        clear(state) {
            state.keys = [];
            state.archiveKeys = [];
            state.asyncGoals = {};
            state.asyncHistory = {};
        },
    },
};